import { vec3 } from 'gl-matrix';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { VertexBuffer } from '../mesh';
import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from '../object';
import { vec4 } from 'gl-matrix';
import { DirectionalLight } from './DirectionalLight';
import { PointLight } from './PointLight';
import { SpotLight } from './SpotLight';

const SELECTED_COLOR: [number, number, number, number] = [1.0, 0.85, 0.1, 1.0];
const ICON_COLOR: [number, number, number, number] = [1.0, 0.95, 0.5, 1.0];
const WIRE_COLOR: [number, number, number, number] = [1.0, 0.9, 0.3, 0.6];
const INNER_COLOR: [number, number, number, number] = [1.0, 0.6, 0.1, 0.5];
const DIR_ARROW_COLOR: [number, number, number, number] = [1.0, 0.95, 0.4, 1.0];

/**
 * Base class for scene-editor light debug shapes. Renders line geometry in Overlay
 * and participates in Picking so a click selects the light.
 */
export abstract class LightVisualizer implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    order: 6000,
    culling: 'none',
    static: false,
    modes: [RenderMode.Overlay, RenderMode.Picking],
  };
  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  public selected = false;
  /** Hide the visualizer without removing it from the scene — used by the scene
   *  editor so non-Lights modes don't draw or pick light icons. Defaults to
   *  hidden; the editor's Lights mode toggles them on. */
  public visible = false;
  /** Stable id (matches the SceneLight.name in the editor) so callers can map a pick back to a light. */
  public readonly lightName: string;

  protected lineBuffer: VertexBuffer;
  protected lineVao: WebGLVertexArrayObject;
  /** Solid pick-volume geometry: triangle list of position-only vertices.
   *  Drawn in the picking pass instead of the line buffer so the user can
   *  click anywhere inside the light's volume (sphere / cone / icon sphere)
   *  rather than aiming at a wireframe edge. */
  protected pickBuffer: VertexBuffer;
  protected pickVao: WebGLVertexArrayObject;
  /** Picking radius around icon center for ray picking fallback (meters). */
  public iconPickRadius = 0.25;

  constructor(
    protected ctx: Context,
    lightName: string,
  ) {
    this.lightName = lightName;
    const gl = ctx.gl;
    this.lineBuffer = new VertexBuffer(gl.createBuffer()!, gl.LINES, 7, 0);
    this.lineVao = this.createLineVAO(this.lineBuffer);
    this.pickBuffer = new VertexBuffer(gl.createBuffer()!, gl.TRIANGLES, 3, 0);
    this.pickVao = this.createPickVAO(this.pickBuffer);
  }

  /** Subclasses produce the line data (flat array of [x y z r g b a] pairs of endpoints). */
  protected abstract buildLines(): number[];

  /** Subclasses produce solid triangle geometry that covers the light volume.
   *  Flat array of [x y z] triplets, three per triangle. */
  protected abstract buildPickVolume(): number[];

  public markDirty(): void {
    const gl = this.ctx.gl;

    const lineData = this.buildLines();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer.glVertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(lineData), gl.DYNAMIC_DRAW);
    this.lineBuffer.itemCount = lineData.length / 7;

    const pickData = this.buildPickVolume();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.pickBuffer.glVertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pickData), gl.DYNAMIC_DRAW);
    this.pickBuffer.itemCount = pickData.length / 3;
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings): void {
    if (!this.visible) return;

    const gl = ctx.gl;

    if (settings.mode === RenderMode.Picking && settings.pickingReference != null) {
      if (this.pickBuffer.itemCount > 0) {
        this.renderPicking(ctx, camera, settings.pickingReference);
      }
      return;
    }
    if (settings.mode !== RenderMode.Overlay) return;
    if (this.lineBuffer.itemCount === 0) return;

    const shader = ctx.engine.shaderManager.getTestGeometryShader();
    ctx.setShader(shader);
    shader.uniformsContainer
      .setModelViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.lineVao);
    gl.drawArrays(gl.LINES, 0, this.lineBuffer.itemCount);
    gl.disable(gl.BLEND);
    gl.enable(gl.DEPTH_TEST);
  }

  private renderPicking(ctx: Context, camera: Camera, pickingReference: number): void {
    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getPickingShader();
    ctx.setShader(shader);
    shader.uniformsContainer
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(camera.modelViewMatrix)
      .setPickingColor(
        vec4.fromValues(
          (pickingReference & 0xff) / 255,
          ((pickingReference >> 8) & 0xff) / 255,
          ((pickingReference >> 16) & 0xff) / 255,
          1,
        ),
      );

    // Solid volume covers the whole light: the user can click anywhere inside
    // the sphere / cone, not just on a wire. Front-face culling so the inside
    // of a back-facing sphere/cone wall doesn't shadow itself when the camera
    // is inside the volume. Depth test stays on so closer objects (other lights,
    // scene geometry) win over a large enclosing pick volume.
    gl.bindVertexArray(this.pickVao);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.FRONT);
    gl.drawArrays(gl.TRIANGLES, 0, this.pickBuffer.itemCount);
    gl.cullFace(gl.BACK);
    gl.disable(gl.CULL_FACE);
  }

  public destroy(): void {
    const gl = this.ctx.gl;
    gl.deleteBuffer(this.lineBuffer.glVertexBuffer);
    gl.deleteVertexArray(this.lineVao);
    gl.deleteBuffer(this.pickBuffer.glVertexBuffer);
    gl.deleteVertexArray(this.pickVao);
  }

  protected pickColor(base: [number, number, number, number]): [number, number, number, number] {
    return this.selected ? SELECTED_COLOR : base;
  }

  protected pushSegment(
    data: number[],
    a: vec3,
    b: vec3,
    color: [number, number, number, number],
  ): void {
    data.push(a[0], a[1], a[2], ...color);
    data.push(b[0], b[1], b[2], ...color);
  }

  /** Push a closed line-strip circle of N segments in the plane defined by u,v axes. */
  protected pushCircle(
    data: number[],
    center: vec3,
    u: vec3,
    v: vec3,
    radius: number,
    color: [number, number, number, number],
    segments = 32,
  ): void {
    const prev = vec3.create();
    const curr = vec3.create();
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2;
      const c = Math.cos(t) * radius;
      const s = Math.sin(t) * radius;
      vec3.set(
        curr,
        center[0] + u[0] * c + v[0] * s,
        center[1] + u[1] * c + v[1] * s,
        center[2] + u[2] * c + v[2] * s,
      );
      if (i > 0) this.pushSegment(data, prev, curr, color);
      vec3.copy(prev, curr);
    }
  }

  /** Push a triangulated UV-sphere (`rings` × `sectors` quads, 2 triangles each).
   *  Triangles are wound CCW from outside so default back-face culling keeps the
   *  outer shell when the camera is outside the volume. */
  protected pushSphere(
    data: number[],
    center: vec3,
    radius: number,
    rings = 8,
    sectors = 16,
  ): void {
    const v = (theta: number, phi: number): [number, number, number] => [
      center[0] + radius * Math.sin(theta) * Math.cos(phi),
      center[1] + radius * Math.cos(theta),
      center[2] + radius * Math.sin(theta) * Math.sin(phi),
    ];
    for (let r = 0; r < rings; r++) {
      const t1 = (r / rings) * Math.PI;
      const t2 = ((r + 1) / rings) * Math.PI;
      for (let s = 0; s < sectors; s++) {
        const p1 = (s / sectors) * 2 * Math.PI;
        const p2 = ((s + 1) / sectors) * 2 * Math.PI;
        const a = v(t1, p1);
        const b = v(t2, p1);
        const c = v(t2, p2);
        const d = v(t1, p2);
        data.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
        data.push(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2]);
      }
    }
  }

  /** Push a solid cone: apex at `pos`, axis `dir` (unit), length `len`,
   *  half-angle `halfAngleDeg`. Triangulated side surface + capped base. */
  protected pushSolidCone(
    data: number[],
    pos: vec3,
    dir: vec3,
    len: number,
    halfAngleDeg: number,
    segments = 24,
  ): void {
    const { u, v } = orthoBasis(dir);
    const baseCenter = vec3.scaleAndAdd(vec3.create(), pos, dir, len);
    const radius = len * Math.tan((halfAngleDeg * Math.PI) / 180);
    const ring: vec3[] = [];
    for (let i = 0; i < segments; i++) {
      const t = (i / segments) * 2 * Math.PI;
      const p = vec3.create();
      vec3.scaleAndAdd(p, baseCenter, u, Math.cos(t) * radius);
      vec3.scaleAndAdd(p, p, v, Math.sin(t) * radius);
      ring.push(p);
    }
    for (let i = 0; i < segments; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % segments];
      data.push(pos[0], pos[1], pos[2], b[0], b[1], b[2], a[0], a[1], a[2]);
    }
    for (let i = 1; i < segments - 1; i++) {
      const a = ring[0];
      const b = ring[i];
      const c = ring[i + 1];
      data.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    }
  }

  private createLineVAO(buffer: VertexBuffer): WebGLVertexArrayObject {
    const gl = this.ctx.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer.glVertexBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 7 * 4, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, 7 * 4, 3 * 4);
    gl.bindVertexArray(null);
    return vao;
  }

  private createPickVAO(buffer: VertexBuffer): WebGLVertexArrayObject {
    const gl = this.ctx.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer.glVertexBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 3 * 4, 0);
    gl.bindVertexArray(null);
    return vao;
  }
}

/** Build an orthonormal basis (u, v) whose axis is `n`. */
function orthoBasis(n: vec3): { u: vec3; v: vec3 } {
  const up = Math.abs(n[1]) < 0.9 ? vec3.fromValues(0, 1, 0) : vec3.fromValues(1, 0, 0);
  const u = vec3.cross(vec3.create(), n, up);
  vec3.normalize(u, u);
  const v = vec3.cross(vec3.create(), n, u);
  vec3.normalize(v, v);
  return { u, v };
}

const ICON_HALF = 0.12;

/** Short cross + wire circle at the light position so the user has a clickable anchor. */
function pushIcon(
  visualizer: LightVisualizer,
  data: number[],
  center: vec3,
  color: [number, number, number, number],
  size = ICON_HALF,
): void {
  visualizer['pushSegment'](
    data,
    vec3.fromValues(center[0] - size, center[1], center[2]),
    vec3.fromValues(center[0] + size, center[1], center[2]),
    color,
  );
  visualizer['pushSegment'](
    data,
    vec3.fromValues(center[0], center[1] - size, center[2]),
    vec3.fromValues(center[0], center[1] + size, center[2]),
    color,
  );
  visualizer['pushSegment'](
    data,
    vec3.fromValues(center[0], center[1], center[2] - size),
    vec3.fromValues(center[0], center[1], center[2] + size),
    color,
  );
}

export class DirectionalLightVisualizer extends LightVisualizer {
  /** Directional lights have no spatial volume; give them a 1 m pick sphere
   *  centered at the icon position so the user can grab them in 3D. */
  private static readonly PICK_RADIUS = 1.0;

  constructor(
    ctx: Context,
    lightName: string,
    private light: DirectionalLight,
  ) {
    super(ctx, lightName);
    this.iconPickRadius = 0.35;
    this.markDirty();
  }

  protected buildPickVolume(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    this.pushSphere(data, pos, DirectionalLightVisualizer.PICK_RADIUS, 8, 16);
    return data;
  }

  protected buildLines(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    const dir = vec3.normalize(vec3.create(), this.light.direction as vec3);
    const iconColor = this.pickColor(ICON_COLOR);
    const arrowColor = this.pickColor(DIR_ARROW_COLOR);

    // Sun glyph: circle perpendicular to direction + radial rays
    const { u, v } = orthoBasis(dir);
    this.pushCircle(data, pos, u, v, 0.25, iconColor, 24);
    for (let i = 0; i < 8; i++) {
      const t = (i / 8) * Math.PI * 2;
      const inner = vec3.create();
      const outer = vec3.create();
      vec3.scaleAndAdd(inner, pos, u, Math.cos(t) * 0.3);
      vec3.scaleAndAdd(inner, inner, v, Math.sin(t) * 0.3);
      vec3.scaleAndAdd(outer, pos, u, Math.cos(t) * 0.5);
      vec3.scaleAndAdd(outer, outer, v, Math.sin(t) * 0.5);
      this.pushSegment(data, inner, outer, iconColor);
    }

    // Direction arrow: shaft + arrowhead
    const arrowLen = 1.5;
    const tip = vec3.scaleAndAdd(vec3.create(), pos, dir, arrowLen);
    this.pushSegment(data, pos, tip, arrowColor);

    // Arrowhead as 4 back-leaning line segments
    const head = 0.2;
    const back = vec3.scaleAndAdd(vec3.create(), tip, dir, -head);
    for (let i = 0; i < 4; i++) {
      const t = (i / 4) * Math.PI * 2;
      const off = vec3.create();
      vec3.scaleAndAdd(off, back, u, Math.cos(t) * head * 0.5);
      vec3.scaleAndAdd(off, off, v, Math.sin(t) * head * 0.5);
      this.pushSegment(data, tip, off, arrowColor);
    }
    return data;
  }
}

export class PointLightVisualizer extends LightVisualizer {
  /** Minimum pick radius so a point light with a tiny outerRange is still
   *  clickable in 3D — the user shouldn't need to aim at the icon cross. */
  private static readonly MIN_PICK_RADIUS = 0.5;

  constructor(
    ctx: Context,
    lightName: string,
    private light: PointLight,
  ) {
    super(ctx, lightName);
    this.markDirty();
  }

  protected buildPickVolume(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    const radius = Math.max(this.light.outerRange, PointLightVisualizer.MIN_PICK_RADIUS);
    this.pushSphere(data, pos, radius, 8, 16);
    return data;
  }

  protected buildLines(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    const iconColor = this.pickColor(ICON_COLOR);
    const wireColor = this.pickColor(WIRE_COLOR);
    const innerColor = this.pickColor(INNER_COLOR);

    pushIcon(this, data, pos, iconColor);

    // Three great-circle wireframe rings
    const axes: [vec3, vec3][] = [
      [vec3.fromValues(1, 0, 0), vec3.fromValues(0, 1, 0)],
      [vec3.fromValues(1, 0, 0), vec3.fromValues(0, 0, 1)],
      [vec3.fromValues(0, 1, 0), vec3.fromValues(0, 0, 1)],
    ];
    for (const [u, v] of axes) {
      this.pushCircle(data, pos, u, v, this.light.outerRange, wireColor, 32);
    }
    if (this.light.innerRange > 0.01 && this.light.innerRange < this.light.outerRange) {
      for (const [u, v] of axes) {
        this.pushCircle(data, pos, u, v, this.light.innerRange, innerColor, 24);
      }
    }
    return data;
  }
}

export class SpotLightVisualizer extends LightVisualizer {
  constructor(
    ctx: Context,
    lightName: string,
    private light: SpotLight,
  ) {
    super(ctx, lightName);
    this.markDirty();
  }

  protected buildPickVolume(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    const dir = vec3.normalize(vec3.create(), this.light.direction as vec3);
    this.pushSolidCone(data, pos, dir, this.light.outerRange, this.light.dotOuterLimit, 24);
    return data;
  }

  protected buildLines(): number[] {
    const data: number[] = [];
    const pos = vec3.clone(this.light.position as vec3);
    const dir = vec3.normalize(vec3.create(), this.light.direction as vec3);
    const iconColor = this.pickColor(ICON_COLOR);
    const wireColor = this.pickColor(WIRE_COLOR);
    const innerColor = this.pickColor(INNER_COLOR);

    pushIcon(this, data, pos, iconColor);

    // Outer cone
    this.pushCone(data, pos, dir, this.light.outerRange, this.light.dotOuterLimit, wireColor);
    // Inner cone (if smaller and non-zero)
    if (
      this.light.dotInnerLimit > 0.01 &&
      this.light.dotInnerLimit < this.light.dotOuterLimit
    ) {
      this.pushCone(data, pos, dir, this.light.outerRange, this.light.dotInnerLimit, innerColor);
    }
    return data;
  }

  /** Wireframe cone: apex at pos, axis dir, length len, half-angle halfAngleDeg. */
  private pushCone(
    data: number[],
    pos: vec3,
    dir: vec3,
    len: number,
    halfAngleDeg: number,
    color: [number, number, number, number],
  ): void {
    const { u, v } = orthoBasis(dir);
    const baseCenter = vec3.scaleAndAdd(vec3.create(), pos, dir, len);
    const radius = len * Math.tan((halfAngleDeg * Math.PI) / 180);

    this.pushCircle(data, baseCenter, u, v, radius, color, 32);

    // 8 apex-to-rim rays
    for (let i = 0; i < 8; i++) {
      const t = (i / 8) * Math.PI * 2;
      const rim = vec3.create();
      vec3.scaleAndAdd(rim, baseCenter, u, Math.cos(t) * radius);
      vec3.scaleAndAdd(rim, rim, v, Math.sin(t) * radius);
      this.pushSegment(data, pos, rim, color);
    }
  }
}
